import { Delete, Lock, Loop, PermIdentityOutlined, PublishedWithChanges } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapSkill from '@src/APIs/graphQl/Skill/changeMapSkill';
import { ShareDialog } from '@src/App/Components/Buttons/ShareButton/ShareButton';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { useMapSkillDialog } from '@src/App/Components/Dialogs/MapSkillDialog/MapSkillDialog';
import { useQuestionEmployeeSkillDialog } from '@src/App/Components/Dialogs/QuestionEmployeeSkill/QuestionEmployeeSkill';
import SkillOverlay from '@src/App/Components/Overlays/SkillOverlay/SkillOverlay';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import { CircleMinus, CirclePlus, Share2 } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ItemBadge, { NewBadge } from '../../ItemBadge/ItemBadge';
import LevelCompareBadge from '../../ItemBadge/LevelCompareBadge';
import SegmentLevelBadge from '../../ItemBadge/SegmentLevelBadge';
import Item from '../Item/Item';
import type { IItemAction } from '../Item/ItemAction';

import styles from './SkillItem.module.css';

interface SkillItemProps {
    title: string;
    id?: number;
    level?: number;
    wantedLevel?: number;
    count?: number;
    disableOverlay?: boolean;
    locked?: boolean;
    lockedBy?: string;
    employeeId?: number;
    newPrefix?: true;
    onClick?: string | (() => void);
    onDelete?: () => void;
    levelBadgeVariant?: 'segment' | 'chip' | 'progress' | 'arrow' | 'circle';
    skillContext?: 'covered' | 'missing';
}

/**
 * Represents a skill item component.
 *
 * @param {SkillItemProps} props - The properties for the skill item component.
 * @returns {JSX.Element} - The rendered skill item component.
 */
function SkillItem({
    title,
    id,
    level,
    wantedLevel,
    count,
    disableOverlay,
    locked,
    lockedBy,
    newPrefix,
    onClick,
    employeeId,
    onDelete,
    levelBadgeVariant = 'segment',
    skillContext,
}: SkillItemProps) {
    const navigate = useNavigate();
    const questionEmployeeSkillDialog = useQuestionEmployeeSkillDialog();
    const mapSkillDialog = useMapSkillDialog();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);

    const skillDetail = useSelector((state: AppState) => state.skills.find((as) => as.id === id));

    const employee = useSelector((state: AppState) => state.employee);
    const [updating, setUpdating] = React.useState('');
    const [overlayOpen, setOverlayOpen] = React.useState(false);
    const own = employee.skills.find((oc) => oc.id === id);

    const inOverlay = disableOverlay ? disableOverlay : false;
    const titleLevel = levelDefinitions[level ? level - 1 : 0]?.title;
    const titleWantedLevel = levelDefinitions[wantedLevel ? wantedLevel - 1 : 0]?.title;
    const maxLevel = levelDefinitions.length;
    let percent = 75;
    if (wantedLevel !== undefined && level !== undefined) {
        const levelDiff = level - wantedLevel;
        percent = (levelDiff / maxLevel) * 100;
    } else if (level !== undefined) {
        percent = (level / maxLevel) * 100;
    }

    const laodingIcon = <Loop fontSize="small" />;
    const notEditAndKnownSkill = onDelete === undefined && id;
    const actions: IItemAction[] = [];
    if (notEditAndKnownSkill) {
        //Nicht Edit Mode und ist im System bekannt
        if (own) {
            //Nicht Edit Mode und ist im System bekannt Und Verknüpft mit Emp
            actions.push({
                id: 'remove',
                icon: updating === 'remove' ? laodingIcon : <CircleMinus size={16} />,
                helper: langStrings.removeButtonHelper,
                text: langStrings.removeButtonTitle,
                onClick: () => {
                    setUpdating('remove');
                    changeMapSkill(id, false).finally(() => {
                        setUpdating('');
                    });
                },
            });
            actions.push({
                id: 'edit',
                icon: updating === 'edit' ? laodingIcon : getAppIcon(EIconCategory.LEVELCHANGE),
                text: langStrings.editButtonTitle,
                helper: langStrings.editButtonHelper,
                onClick: () => {
                    setUpdating('edit');
                    mapSkillDialog(id).finally(() => {
                        setUpdating('');
                    });
                },
            });
        } else {
            actions.push({
                id: 'add',
                icon: updating === 'add' ? laodingIcon : <CirclePlus size={16} />,
                text: langStrings.addButtonTitle,
                helper: langStrings.addButtonHelper,
                onClick: () => {
                    setUpdating('add');
                    mapSkillDialog(id).finally(() => {
                        setUpdating('');
                    });
                },
            });
        }
        if (employeeId && employeeId !== employee.id) {
            actions.push({
                id: 'toQuestion',
                icon: updating === 'toQuestion' ? laodingIcon : <PublishedWithChanges fontSize="small" />,
                text: langStrings.questionSkillTitle,
                helper: langStrings.questionSkillHelper,
                onClick: () => {
                    setUpdating('toQuestion');
                    questionEmployeeSkillDialog(employeeId, id, level).finally(() => {
                        setUpdating('');
                    });
                },
            });
        }
        if (skillDetail) {
            //TODO: Wenn ID, dann Laden und dann dialog öffnen... oder im Dialog laden lassen
            actions.push({
                id: 'share',
                icon: <Share2 size={16} />,
                text: langStrings.oftenUsed.share,
                helper: langStrings.oftenUsed.share,
                onClick: () => {
                    openDialog(<ShareDialog id={'ShareDialogSkill'} entity={skillDetail} resolve={() => {}} />);
                    // showShareDialog(skillDetail)
                },
            });
        }
    }

    // Kontextbasierte Level-Bestimmung für Textfarbe
    const getEffectiveLevel = () => {
        const isLevelNotReached = level !== undefined && wantedLevel !== undefined && level < wantedLevel;

        if (skillContext === 'missing' || isLevelNotReached) {
            // Missing Skills oder Level nicht erreicht: Immer schwarze Schrift
            return 2;
        }
        if (skillContext === 'covered') {
            // Covered Skills: Verwende aktuelles Level für Textfarbe
            return level;
        }
        // Standard: Verwende Level >= 3 Logik
        return level;
    };

    const order = wantedLevel ? wantedLevel * -1 : level ? level * -1 : undefined;
    const handleSingleClick = () => {
        if (id && !disableOverlay) {
            setOverlayOpen(true);
        }
    };

    const handleDoubleClick = () => {
        if (onClick) {
            if (typeof onClick === 'string') {
                navigate(onClick);
            } else {
                onClick();
            }
        }
    };

    const itemContent = (
        <Item
            onClick={handleSingleClick}
            onDoubleClick={handleDoubleClick}
            percent={percent}
            order={order}
            level={getEffectiveLevel()}
            actions={actions}
            leftContent={
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {(level !== undefined || wantedLevel !== undefined) && levelBadgeVariant === 'segment' && (
                        <>
                            {level !== undefined && (
                                <SegmentLevelBadge
                                    level={level}
                                    title={`${langStrings.oftenUsed.level}: ${titleLevel}`}
                                />
                            )}
                            {wantedLevel !== undefined && (
                                <SegmentLevelBadge
                                    level={wantedLevel}
                                    title={`${langStrings.wantedLevel}: ${titleWantedLevel}`}
                                />
                            )}
                        </>
                    )}
                    {(level !== undefined || wantedLevel !== undefined) && levelBadgeVariant !== 'segment' && (
                        <LevelCompareBadge
                            level={level}
                            wantedLevel={wantedLevel}
                            variant={levelBadgeVariant}
                            title={
                                wantedLevel
                                    ? `${langStrings.oftenUsed.level}: ${titleLevel} / ${langStrings.wantedLevel}: ${titleWantedLevel}`
                                    : `${langStrings.oftenUsed.level}: ${titleLevel}`
                            }
                            size="small"
                        />
                    )}
                    {count !== undefined && (
                        <ItemBadge
                            value={count}
                            title={langStrings.count}
                            icon={<PermIdentityOutlined />}
                            percent={count > 0 ? Math.min(count * 10, 100) : 0}
                        />
                    )}
                </Box>
            }
            rightContent={
                <>
                    {onDelete !== undefined && (
                        <div className="skillItemDelete" title={langStrings.delete}>
                            <IconButton onClick={onDelete} size="small">
                                <Delete fontSize="small" />
                            </IconButton>
                        </div>
                    )}
                    {(locked !== undefined || lockedBy !== undefined) && (
                        <div
                            className={styles.itemLocked}
                            title={
                                lockedBy
                                    ? `${langStrings.oftenUsed.lockedBy}: "${lockedBy}"`
                                    : langStrings.oftenUsed.locked
                            }
                        >
                            <Lock fontSize="small" />
                        </div>
                    )}
                </>
            }
        >
            {title}
            {newPrefix && <NewBadge />}
        </Item>
    );

    return !id || inOverlay ? (
        itemContent
    ) : (
        <SkillOverlay key={id} disabled={false} skillId={id} open={overlayOpen} onClose={() => setOverlayOpen(false)}>
            {itemContent}
        </SkillOverlay>
    );
}
export default SkillItem;
